/**
 * ArRowCheckbox Component
 *
 * Checkbox for selecting a row in ArTable, plus the row's drag handle.
 * Uses Valtio state from ArTableUIStateContext for selection.
 * Supports shift+click for range selection.
 * Invariant: this cell never calls `useSortable` itself. `RegisterDroppable` is
 * last-write-wins per id, so a second registration on the row's id would race
 * `ArRow`'s — the one that owns the node ref — and could leave the row a
 * droppable with no element. `ArRow` owns the registration and hands the
 * activator props down (SYD-2406).
 * Invariant: `ArRow` disables the draggable on a locked row, so `@dnd-kit`
 * hands down `dragListeners: undefined` — the grip must read disabled rather
 * than look live and do nothing.
 * Invariant: ticking is refused while `isDispatchingWorkflowRun` is set — the
 * per-recipient documents a workflow run is building read the live ticks, so a
 * tick landing mid-run empties every document built after it (SYD-2410). `ArRow`
 * renders no checkbox at all in PDF export mode, so the portal's own copies never
 * see the lock.
 * In card mode (below `md`) the cell is the card's header: the grip sits inline
 * beside the checkbox at full control height, a touch target rather than a
 * 10px sliver. The grip sets `touch-action: none` in both modes so a finger drag
 * reaches dnd-kit's pointer sensor instead of scrolling the page.
 */

import { FC, memo, CSSProperties, MouseEvent, useContext } from 'react';
import { Button, Checkbox, theme } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { ArTableUIStateContext } from '../context';
import { formStore } from '@/store';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import { AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import type { ArRowId } from '#types/System.Artifacts.types';

type ArRowCheckboxProps = {
  rowId: ArRowId;
  isOddRow?: boolean;
  isRowLocked?: boolean;
  stickyFirstColumn?: boolean;
  manualReorderEnabled?: boolean;
  indented?: boolean;
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
  cardMode?: boolean;
};

/**
 * Checkbox cell for row selection.
 * Sticky left when stickyFirstColumn is enabled.
 */
const ArRowCheckbox: FC<ArRowCheckboxProps> = ({
  rowId,
  isOddRow = false,
  isRowLocked = false,
  stickyFirstColumn = true,
  manualReorderEnabled = false,
  indented = false,
  dragAttributes,
  dragListeners,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const uiState = useContext(ArTableUIStateContext)!;

  const selectedRowsSnap = useSnapshot(uiState.selection.selectedRows);
  const isSelected = selectedRowsSnap[rowId] ?? false;
  const { isDispatchingWorkflowRun } = useSnapshot(formStore.state);

  /**
   * Handles checkbox click to toggle row selection.
   * Supports shift+click for range selection. Mutates the Valtio proxy
   * directly — the intended pattern.
   */
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (isDispatchingWorkflowRun) return;

    const selection = uiState.selection;
    /**
     * A hand tick redefines the selection, so it cancels any escalation to the
     * whole table: "all rows" was a statement about the set that existed when
     * the user made it (SYD-2386).
     */
    selection.scope = 'visible';

    if (e.shiftKey && selection.lastClickedRowId !== null) {
      const rangeIds = uiState.renderedRowIds;

      const lastIndex = rangeIds.indexOf(selection.lastClickedRowId);
      const currentIndex = rangeIds.indexOf(rowId);

      if (lastIndex !== -1 && currentIndex !== -1) {
        const startIndex = Math.min(lastIndex, currentIndex);
        const endIndex = Math.max(lastIndex, currentIndex);

        for (let i = startIndex; i <= endIndex; i++) {
          if (selection.selectedRowIds.length >= AR_TABLE_PAGE_SIZE) break;
          const id = rangeIds[i] as ArRowId;
          if (id === AR_TABLE_AGGREGATIONS_ROW_ID) continue;
          if (selection.selectedRows[id] !== true) {
            selection.selectedRows[id] = true;
            selection.selectedRowIndexById[id] = selection.selectedRowIds.length;
            selection.selectedRowIds.push(id);
            selection.selectedCount++;
          }
        }
      }
    } else {
      // Toggle single row
      if (selection.selectedRows[rowId] === true) {
        const idx = selection.selectedRowIndexById[rowId];
        if (idx !== undefined) {
          const lastIndex = selection.selectedRowIds.length - 1;
          const lastRowId = selection.selectedRowIds[lastIndex] as ArRowId;
          if (idx !== lastIndex) {
            selection.selectedRowIds[idx] = lastRowId;
            selection.selectedRowIndexById[lastRowId] = idx;
          }
          selection.selectedRowIds.pop();
        }
        delete selection.selectedRowIndexById[rowId];
        delete selection.selectedRows[rowId];
        selection.selectedCount--;
      } else if (selection.selectedRowIds.length < AR_TABLE_PAGE_SIZE) {
        selection.selectedRows[rowId] = true;
        selection.selectedRowIndexById[rowId] = selection.selectedRowIds.length;
        selection.selectedRowIds.push(rowId);
        selection.selectedCount++;
      }
    }

    selection.lastClickedRowId = rowId;
  };

  const cellBackground = isRowLocked
    ? token.colorFillSecondary
    : isOddRow
      ? token.colorBgBase
      : token.colorFillQuaternary;

  const cellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: cardMode ? 'flex-start' : 'center',
    cursor: isRowLocked || isDispatchingWorkflowRun ? 'not-allowed' : undefined,
    backgroundColor: cellBackground,
    borderRight: cardMode ? undefined : `1px solid ${token.colorBorderSecondary}`,
    boxSizing: 'border-box',
    ...(indented ? {
      borderLeft: `3px solid ${token.colorText}`,
    } : null),
    ...(cardMode ? { gridRow: 1, gridColumnStart: 1 } : null),
  };

  if (stickyFirstColumn) {
    cellStyle.position = 'sticky';
    cellStyle.left = 0;
    cellStyle.zIndex = 10;
  }

  // Don't render checkbox for system/internal rows
  if (rowId === AR_TABLE_AGGREGATIONS_ROW_ID) {
    return <div style={cellStyle} />;
  }

  return (
    <div
      role="cell"
      aria-label={`artable-row-checkbox-${rowId}`}
      style={cellStyle}
      onClick={handleClick}
    >
      <div
        style={
          cardMode
            ? { display: 'flex', alignItems: 'center', gap: token.marginXS }
            : { position: 'relative', width: '100%', height: '100%' }
        }
      >
        {manualReorderEnabled && (
          <Button
            {...dragAttributes}
            {...dragListeners}
            type="text"
            size="small"
            disabled={isRowLocked}
            aria-label={`artable-row-drag-handle-${rowId}`}
            icon={<HolderOutlined style={{ fontSize: token.fontSizeSM }} />}
            style={{
              ...(cardMode
                ? {
                    width: token.controlHeight,
                    minWidth: token.controlHeight,
                    height: token.controlHeight,
                  }
                : {
                    position: 'absolute',
                    right: 1,
                    top: 6,
                    width: 10,
                    minWidth: 10,
                    height: 18,
                  }),
              padding: 0,
              touchAction: 'none',
              cursor: isRowLocked ? 'not-allowed' : 'grab',
              color: isRowLocked ? token.colorTextDisabled : token.colorTextSecondary,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Checkbox
            checked={isSelected}
            disabled={isDispatchingWorkflowRun}
            aria-label={`select-row-${rowId}`}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(ArRowCheckbox);
